// PostInteractionsBar — Like / Repost / Favourite / Share + analytics popover.
// Drop this onto every newsfeed post. Hits `/api/posts/{id}/{action}` and
// keeps optimistic UI in sync with server state.
import { useEffect, useRef, useState } from "react";
import { Heart, Repeat2, Bookmark, Share2, BarChart3, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import InteractorsModal from "./InteractorsModal";
// BoostButton removed in Round J — creators now earn a 45% share of ad revenue automatically.

function InteractionBtn({ Icon, count, active, activeCls, onClick, testid, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${
        active ? activeCls : "text-white/55 hover:bg-white/5 hover:text-white"
      }`}
      title={label}
    >
      <Icon className={`w-4 h-4 ${active ? "fill-current" : ""}`} />
      <span className="text-[11px] font-display tracking-wider">{count.toLocaleString()}</span>
    </button>
  );
}

export default function PostInteractionsBar({ post, onChange }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    counts: {
      likes: post.like_count || post.likes || 0,
      reposts: post.repost_count || 0,
      favourites: post.favourite_count || 0,
      shares: post.share_count || 0,
      views: post.view_count || 0,
    },
    my_state: { liked: false, favourited: false, reposted: false },
    is_author_or_owner: false,
  });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [interactorsKind, setInteractorsKind] = useState(null);
  const barRef = useRef(null);
  const [addedToStory, setAddedToStory] = useState(() => {
    if (post.kind === "story") return true;
    try {
      const seen = JSON.parse(sessionStorage.getItem("nexus_posts_in_story") || "[]");
      return seen.includes(post.id);
    } catch { return false; }
  });
  const isAuthor = user && post.user_id === user.id;

  useEffect(() => {
    api.get(`/posts/${post.id}/analytics`)
      .then(({ data }) => setState(data))
      .catch(() => {});
  }, [post.id]);

  // Fire a `/view` interaction the first time this post scrolls into the
  // viewport for at least 400ms. Server now dedupes per user, so repeated
  // calls are harmless, but we still gate client-side to save network.
  useEffect(() => {
    const el = barRef.current; if (!el || typeof IntersectionObserver === "undefined") return;
    let fired = false;
    let timer = null;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio > 0.4 && !fired) {
          timer = setTimeout(() => {
            fired = true;
            api.post(`/posts/${post.id}/view`)
              .then(() => setState((s) => ({ ...s, counts: { ...s.counts, views: s.counts.views + 1 } })))
              .catch(() => {});
            io.disconnect();
          }, 400);
        } else if (!e.isIntersecting && timer) { clearTimeout(timer); timer = null; }
      }
    }, { threshold: [0, 0.4, 1] });
    io.observe(el);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [post.id]);

  const requireAuth = () => {
    if (user) return true;
    toast.error("Sign in to react");
    return false;
  };

  const hit = async (kind) => {
    if (kind !== "share" && !requireAuth()) return;
    try {
      const { data } = await api.post(`/posts/${post.id}/${kind}`);
      if (kind === "like" || kind === "favourite" || kind === "repost") {
        setState((s) => ({
          ...s,
          counts: { ...s.counts, [`${kind}s`]: Math.max(0, s.counts[`${kind}s`] + (data.active ? 1 : -1)) },
          my_state: { ...s.my_state, [kind === "like" ? "liked" : `${kind}d`]: data.active },
        }));
        if (kind === "repost" && data.active) onChange?.();
      } else if (kind === "share") {
        setState((s) => ({ ...s, counts: { ...s.counts, shares: s.counts.shares + 1 } }));
        navigator.clipboard?.writeText(`${window.location.origin}/u/${post.user_id}`);
        toast.success("Profile link copied");
      }
    } catch { toast.error("Action failed"); }
  };

  const addToStory = async () => {
    if (!isAuthor) return;
    try {
      const { data } = await api.post(`/posts/${post.id}/add-to-story`);
      setAddedToStory(true);
      try {
        const seen = new Set(JSON.parse(sessionStorage.getItem("nexus_posts_in_story") || "[]"));
        seen.add(post.id);
        sessionStorage.setItem("nexus_posts_in_story", JSON.stringify(Array.from(seen)));
      } catch { /* ignore quota errors */ }
      toast.success(data.already ? "Already in your Story" : "Added to your 24h Story");
      // Refresh stories rail if visible
      window.dispatchEvent(new CustomEvent("nexus:stories-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to story");
    }
  };

  return (
    <div ref={barRef} className="flex flex-wrap items-center gap-1 pt-2 border-t border-white/5" data-testid={`interactions-${post.id}`}>
      <InteractionBtn Icon={Heart} count={state.counts.likes} active={state.my_state.liked} activeCls="bg-nexus-pink/10 text-nexus-pink" onClick={() => hit("like")} testid={`post-like-${post.id}`} label="Like" />
      <InteractionBtn Icon={Repeat2} count={state.counts.reposts} active={state.my_state.reposted} activeCls="bg-nexus-mint/10 text-nexus-mint" onClick={() => hit("repost")} testid={`post-repost-${post.id}`} label="Repost" />
      <InteractionBtn Icon={Bookmark} count={state.counts.favourites} active={state.my_state.favourited} activeCls="bg-nexus-cyan/10 text-nexus-cyan" onClick={() => hit("favourite")} testid={`post-favourite-${post.id}`} label="Favourite" />
      <InteractionBtn Icon={Share2} count={state.counts.shares} active={false} activeCls="" onClick={() => hit("share")} testid={`post-share-${post.id}`} label="Share" />
      <span className="ml-auto flex items-center gap-2">
        <button
          onClick={() => setInteractorsKind("like")}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] text-white/50 hover:text-white hover:bg-white/5 transition"
          data-testid={`interactors-open-${post.id}`}
          title="Who liked / viewed / shared"
        >
          <Eye className="w-3.5 h-3.5" />
          <span className="font-display tracking-wider">{(state.counts.views || 0).toLocaleString()}</span>
        </button>
        {isAuthor && post.kind !== "story" && (
          <button
            onClick={addToStory}
            disabled={addedToStory}
            data-testid={`add-to-story-${post.id}`}
            title={addedToStory ? "Already in your 24h Story" : "Add to your 24h Story"}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-display tracking-wider transition-colors ${
              addedToStory
                ? "bg-nexus-gold/15 text-nexus-gold cursor-not-allowed"
                : "bg-white/5 text-white/70 hover:bg-nexus-gold/15 hover:text-nexus-gold"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{addedToStory ? "IN STORY" : "ADD TO STORY"}</span>
          </button>
        )}
        {state.is_author_or_owner && (
          <button onClick={() => setShowAnalytics((v) => !v)} className="rounded-full px-2 py-1 text-white/40 hover:text-white" data-testid={`analytics-${post.id}`} title="Post analytics">
            <BarChart3 className="w-4 h-4" />
          </button>
        )}
        {/* Impression bundles removed in Round J — creators now earn a 45% share of every
            ad shown on their posts automatically via the Nexus Business Suite. */}      </span>
      {showAnalytics && state.is_author_or_owner && (
        <div className="w-full mt-2 rounded-xl border border-white/10 bg-nexus-obsidian/60 px-3 py-2 grid grid-cols-5 gap-2 text-[11px]" data-testid={`analytics-panel-${post.id}`}>
          {[
            { k: "views", label: "Views", c: "text-white" },
            { k: "likes", label: "Likes", c: "text-nexus-pink" },
            { k: "reposts", label: "Reposts", c: "text-nexus-mint" },
            { k: "favourites", label: "Favourites", c: "text-nexus-cyan" },
            { k: "shares", label: "Shares", c: "text-nexus-gold" },
          ].map((s) => (
            <div key={s.k}>
              <div className={`text-base font-display font-bold ${s.c}`}>{state.counts[s.k]}</div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-white/40">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      {interactorsKind && (
        <InteractorsModal
          postId={post.id}
          initialKind={interactorsKind}
          counts={state.counts}
          onClose={() => setInteractorsKind(null)}
        />
      )}
    </div>
  );
}
